import { isUiPayload } from "@agent/uiSchema";
import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import type { ActorKind } from "@replay/replayClient";
import { type CardSeverity, toCardSeverity } from "@subjects/cardSeverity";

/** An entity a task is about (patient / client / visit / …). */
export interface TaskSubject {
  entityId: string;
  kind: components["schemas"]["TaskEntity"]["kind"];
  name: string | null;
  /** The owner's name — only for kind "patient" with a linked client. */
  clientName: string | null;
}

/**
 * Who resolved a closed task (BE-2276) — mirrors brian's `ResolvedBy`
 * (modules/task/schemas.py). `userName` is inline, so no roster lookup.
 */
export interface TaskResolvedBy {
  actorKind: ActorKind;
  userId: string | null;
  userName: string | null;
}

/** A view a sign task acts on — one item of `task.views` (BE-2034). */
export interface TaskViewRef {
  id: string;
  name: string;
  status: string;
}

export interface Task {
  id: string;
  title: string;
  kind: string | null;
  payload: unknown | null;
  status: string;
  assigneeUserId: string | null;
  assignedRoleId: string | null;
  dueAt: string | null;
  /**
   * The agent's status signal for this task's card (BE-1918): `calm` (default,
   * no chrome) / `watch` / `urgent`, with `reason` the one-line "why" shown for
   * watch/urgent.
   */
  severity: CardSeverity;
  reason: string | null;
  createdAt: string;
  updatedAt: string;
  /** When the task went terminal (BE-2200). Not `updatedAt`, which later writes move. */
  closedAt: string | null;
  /**
   * Who moved the task to its terminal status (BE-2276) — mirrors brian's
   * `ResolvedBy` (modules/task/schemas.py). The resolver, not the assignee:
   * `actorKind === "agent"` means Brian closed it itself. Null while open and
   * for tasks closed before the backend recorded attribution.
   */
  resolvedBy: TaskResolvedBy | null;
  about: TaskSubject[];
  /** Views this task acts on — resolved on every task read, the
   * `PATCH /tasks/{uuid}` response included (it runs through
   * `serialize_tasks` like the listings). */
  views: TaskViewRef[];
}

/**
 * Tasks the agent rendered via `render_ui` to ask staff something. The server
 * stamps this kind at creation.
 */
export const NEEDS_CLARIFICATION = "needs_clarification";

/**
 * A human-verified PIMS write (BE-1815). When a clinic runs in `manual`
 * pims_write_mode the agent doesn't drive the PIMS — the server renders the
 * intended write as a `render_ui` checkbox card on a `pims_write` task. So it's
 * an a2ui task like a clarification (`payload` is a UI tree, rendered by
 * `UiRenderer`, closed via `apply_ui_action`); this constant exists only to
 * label it "PIMS write" in the feed.
 */
export const PIMS_WRITE = "pims_write";

/**
 * A plain staff to-do the agent can't carry out itself (BE-1913) — e.g.
 * "reschedule Andy's appointment to 3pm" when no tool edits a visit's time.
 * Unlike the other surfaceable kinds it carries NO a2ui payload, just a title,
 * so the feed renders it as a plain card with Mark-done / Dismiss actions
 * (resolved server-side to `done` / `cancelled`) rather than through
 * `UiRenderer`.
 */
export const HUMAN = "human";

/**
 * A view awaiting a human signature (BE-2034). Unlike every other kind, a sign
 * card must NOT resolve via `PATCH /tasks` (that 422s) — signing needs a staff
 * identity and goes through `POST /views/{uuid}/sign`, which auto-closes the
 * task. Dismiss (decline) still routes through `PATCH /tasks`.
 */
export const SIGN = "sign";

const KIND_LABELS: Record<string, string> = {
  [NEEDS_CLARIFICATION]: "Needs Clarification",
  [PIMS_WRITE]: "PIMS write",
  [HUMAN]: "To-Do",
  [SIGN]: "Sign",
};

/**
 * The staff-actionable kinds, in display order. Mirrors brian's
 * `SURFACEABLE_KINDS` (BE-2381): unfiltered `GET /tasks` and `GET /tasks/counts`
 * return exactly these.
 */
export const KNOWN_TASK_KINDS: string[] = [
  NEEDS_CLARIFICATION,
  PIMS_WRITE,
  HUMAN,
  SIGN,
];

/**
 * A `?kind=` search value if it names a staff-actionable kind, else undefined.
 * Keeps stale URLs (`visit_workflow`, the old `other` bucket) from filtering
 * the /tasks list to agent-internal tasks or a 422 (WEB-3362).
 */
export const toKnownTaskKind = (value: unknown): string | undefined =>
  typeof value === "string" && KNOWN_TASK_KINDS.includes(value)
    ? value
    : undefined;

/** Display label for a task's kind; an unlabelled kind reads as its wire value. */
export const taskKindLabel = (kind: string | null): string =>
  (kind && (KIND_LABELS[kind] ?? kind.replace(/_/g, " "))) || "Task";

const ACTOR_KINDS = new Set(["user", "agent", "device", "system"] as const);

// The wire field is an open string; anything unrecognized degrades to
// "system" (an unattributed machine actor), matching brian's replay mapping.
const toActorKind = (kind: string): ActorKind =>
  ACTOR_KINDS.has(kind as ActorKind) ? (kind as ActorKind) : "system";

const toResolvedBy = (
  r: components["schemas"]["ResolvedBy"] | null | undefined,
): TaskResolvedBy | null =>
  r
    ? {
        actorKind: toActorKind(r.actor_kind),
        userId: r.user_uuid ?? null,
        userName: r.user_name ?? null,
      }
    : null;

const RESOLVER_FALLBACK: Record<ActorKind, string> = {
  user: "Staff",
  agent: "Brian",
  device: "Device",
  system: "System",
};

/**
 * Display name for who resolved a task: the person's name when recorded,
 * otherwise a label for the actor kind — `agent` reads as "Brian" (BE-2276;
 * deliberately diverges from replay's "Companion", see PR). Truthiness, not
 * `??`: the backend doesn't forbid an empty-string name, and "Completed by "
 * is worse than the kind label.
 */
export const resolverDisplayName = (resolvedBy: TaskResolvedBy): string =>
  resolvedBy.userName || RESOLVER_FALLBACK[resolvedBy.actorKind];

/**
 * The task's resolver, only when genuinely identified: Brian, or a person
 * whose name was recorded. A bare kind label ("Completed by Staff") carries
 * strictly less than the assignee badge it would replace — reachable when the
 * resolving user's row is gone (`user_name` null off the outer join) or when
 * an actor kind this build doesn't know degraded to `system` — so those rows
 * keep the pre-attribution display.
 */
export const identifiedResolver = (task: Task): TaskResolvedBy | null =>
  task.resolvedBy &&
  (task.resolvedBy.actorKind === "agent" || task.resolvedBy.userName)
    ? task.resolvedBy
    : null;

export const toTask = (t: components["schemas"]["TaskOut"]): Task => ({
  id: t.uuid,
  title: t.title,
  kind: t.kind,
  payload: t.payload,
  status: t.status,
  assigneeUserId: t.assignee_user_uuid,
  assignedRoleId: t.assigned_role_uuid,
  dueAt: t.due_at,
  severity: toCardSeverity(t.severity),
  reason: t.reason ?? null,
  createdAt: t.created_at,
  updatedAt: t.updated_at,
  closedAt: t.closed_at ?? null,
  resolvedBy: toResolvedBy(t.resolved_by),
  about: (t.about ?? []).map((entity) => ({
    entityId: entity.entity_uuid,
    kind: entity.kind,
    name: entity.name,
    clientName: entity.client_name ?? null,
  })),
  views: (t.views ?? []).map((view) => ({
    id: view.uuid,
    name: view.title,
    status: view.status,
  })),
});

/** The patient a task is about, if any — used to label notifications. */
export const taskPatient = (task: Task): TaskSubject | null =>
  task.about.find((subject) => subject.kind === "patient") ?? null;

/**
 * Soonest-due first, undated last, then oldest-created — the canonical
 * outstanding-task order. Overdue tasks have the earliest due dates, so this
 * floats them to the top of any list.
 */
export const byDueDateThenCreated = (a: Task, b: Task): number => {
  if (a.dueAt && b.dueAt) return a.dueAt.localeCompare(b.dueAt);
  if (a.dueAt) return -1;
  if (b.dueAt) return 1;
  return a.createdAt.localeCompare(b.createdAt);
};

/**
 * A subject's outstanding tasks, soonest-due first (WEB-3199). Excludes
 * answered a2ui tasks: `PATCH /tasks` stamps `submitted_action` while the
 * task stays open until the agent resumes (WEB-3320).
 */
export const outstandingTasks = (tasks: Task[]): Task[] =>
  tasks
    .filter(
      (task) =>
        task.status === "open" &&
        !(isUiPayload(task.payload) && task.payload.submitted_action),
    )
    .sort(byDueDateThenCreated);

/**
 * What the rail's Completed group lists (WEB-3300): the kinds a person closes.
 * Explicit rather than the server default because `sign` resolves through the
 * view, not `PATCH /tasks`, so it stays out of this group.
 */
export const COMPLETED_TASK_KINDS: components["schemas"]["TaskKind"][] = [
  HUMAN,
  PIMS_WRITE,
  NEEDS_CLARIFICATION,
];

/**
 * Clinic-wide open-task counts per kind for the Clinic View summary (BE-1989),
 * so it doesn't derive them from a paginated list: how many are open and how
 * many of those the agent has flagged urgent (BE-2133). Keyed by the surfaceable
 * kinds only (BE-2381), with no `"none"` bucket; the summary sums them for totals.
 */
export type TaskCounts = Record<string, { open: number; urgent: number }>;

/**
 * Task-visibility scope (BE-2089). `me` (the default the backend applies when
 * omitted) returns only the caller's own user/role tasks plus unassigned ones;
 * `all` returns the whole clinic and is honored only for leadership — the
 * backend still filters other callers to their `me` set.
 */
export type AssignedTo = "me" | "all";

export const OPEN_TASKS_PAGE_SIZE = 50;

/**
 * One page of tasks plus the cursor for the page after it. `nextCursor` is an opaque
 * server-minted token (BE-2133): echo it back untouched to fetch the next page, or null
 * when this was the last page. Never build or parse one, since the clinic-wide and
 * subject-scoped listings order differently and mint different shapes, and the wire
 * format is the server's to change.
 */
export interface TaskPage {
  tasks: Task[];
  nextCursor: string | null;
}

/**
 * One page of the clinic's open tasks for the clinic-wide list (WEB-3222), ordered
 * urgent-first then by due date (BE-2133). Every filter is server-side — `status: "open"`
 * plus optional `kind` and `urgent` — so each page holds only matching tasks and
 * pagination stays correct per filter (a count's link can't land on pages of
 * non-matches). `kind` takes one surfaceable kind, omitted for all of them;
 * `cursor` is the previous page's `nextCursor`, omitted for the first page.
 */
export const fetchOpenTasks = (
  cursor?: string,
  kind?: string,
  urgent?: boolean,
  assignedTo?: AssignedTo,
): Promise<TaskPage> =>
  request(() =>
    apiClient.GET("/tasks", {
      params: {
        query: {
          status: "open",
          limit: OPEN_TASKS_PAGE_SIZE,
          cursor,
          // Repeatable since BE-2200, so one kind goes as a one-element list.
          kind:
            kind === undefined
              ? undefined
              : [kind as components["schemas"]["TaskKind"]],
          urgent: urgent || undefined,
          assigned_to: assignedTo,
        },
      },
    }),
  ).then((page) => ({
    tasks: page.items.map(toTask),
    nextCursor: page.next_cursor ?? null,
  }));

export const fetchTaskCounts = (assignedTo?: AssignedTo): Promise<TaskCounts> =>
  request(() =>
    apiClient.GET("/tasks/counts", {
      params: { query: { assigned_to: assignedTo } },
    }),
  ).then((c) => c.by_kind);

export const COMPLETED_TASKS_PAGE_SIZE = 25;

/**
 * A subject's open tasks for the rail's "Needs you" (WEB-3300). Filtered
 * server-side, not just by `outstandingTasks`: this reads one page, so enough
 * closed history in the same window would push an open task off it. Sends no
 * `kind`, so it gets the server default of staff-actionable kinds (BE-2381) -
 * agent-internal tasks on the subject stay out of "Needs you".
 *
 * Still one page, so `OPEN_TASKS_PAGE_SIZE` open tasks on a single patient is a
 * hard ceiling — past it the rest drop off "Needs you" with nothing to say so.
 * Left uncursored deliberately: this sits on the rail's blocking suspense path,
 * and 50 simultaneously-open to-dos for one patient isn't a state worth paying
 * serial round-trips on every subject open to survive.
 */
export const fetchSubjectOpenTasks = (subject: string): Promise<Task[]> =>
  request(() =>
    apiClient.GET("/tasks", {
      params: {
        query: { subject, status: "open", limit: OPEN_TASKS_PAGE_SIZE },
      },
    }),
  ).then((page) => page.items.map(toTask));

/**
 * One page of a subject's completed to-dos (WEB-3300). `status`/`kind` filter
 * server-side (BE-2200), so "Load more" can't return a page of nothing.
 *
 * Newest-raised first, not newest-completed: that's the order the subject
 * listing's cursor mints, and a client re-sort would only reorder loaded pages.
 */
export const fetchCompletedTasks = (
  subject: string,
  cursor?: string,
): Promise<TaskPage> =>
  request(() =>
    apiClient.GET("/tasks", {
      params: {
        query: {
          subject,
          status: "done",
          kind: COMPLETED_TASK_KINDS,
          limit: COMPLETED_TASKS_PAGE_SIZE,
          cursor,
        },
      },
    }),
  ).then((page) => ({
    tasks: page.items.map(toTask),
    nextCursor: page.next_cursor ?? null,
  }));

/**
 * Submit a staff answer to a `render_ui` / clarification task. The backend
 * records `{action, data}` on the task payload and resumes the agent with it.
 */
export const submitTaskAction = (
  id: string,
  action: string,
  data: Record<string, unknown>,
): Promise<Task> =>
  request(() =>
    apiClient.PATCH("/tasks/{task_uuid}", {
      params: { path: { task_uuid: id } },
      body: { action, data },
    }),
  ).then(toTask);
